import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Pie, Chart } from 'react-chartjs-2';
import {
  BoxPlotController,
  BoxAndWiskers,
} from '@sgratzl/chartjs-chart-boxplot';
import auditAPI from '../services/api';
import { Environment } from '../services/api';
import { getDateRangePreset } from '../utils/dateHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BoxPlotController,
  BoxAndWiskers
);

interface ChartDashboardProps {
  dateRange?: { start: string; end: string };
  environment?: Environment;
}

const ChartDashboard: React.FC<ChartDashboardProps> = ({ dateRange, environment = 'prod' }) => {
  const [errorRateData, setErrorRateData] = useState<any>(null);
  const [costData, setCostData] = useState<any>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [histogramData, setHistogramData] = useState<any>(null);
  const [boxPlotData, setBoxPlotData] = useState<any>(null);
  const [requestSizeData, setRequestSizeData] = useState<any>(null);
  const [requestSizeCounts, setRequestSizeCounts] = useState<number[]>([]);
  const [postVsPatchData, setPostVsPatchData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, environment]);

  const loadChartData = async () => {
    setLoading(true);
    try {
      const range = dateRange || getDateRangePreset('last_7d');

      // Fire all Athena queries in parallel — allSettled so one failure doesn't blank all charts
      const settled = await Promise.allSettled([
        auditAPI.getErrorRate(range.start, range.end, environment),
        auditAPI.getCostByCompany(range.start, range.end, environment),
        auditAPI.getPerformanceMetrics(range.start, range.end, environment),
        auditAPI.getAIProcessingTimeDistribution(range.start, range.end, environment),
        auditAPI.getResponseTimeHistogram(range.start, range.end, environment),
        auditAPI.getAvgTimeByRequestSize(range.start, range.end, environment),
        auditAPI.getPostVsPatchByCompany(range.start, range.end, environment),
      ]);
      const unwrap = (r: PromiseSettledResult<any>, fallback: any = []) => {
        if (r.status === 'fulfilled') return r.value;
        console.error('Chart query failed:', (r as PromiseRejectedResult).reason);
        return fallback;
      };
      const [errorRate, costByCompany, performance, aiProcessingTimes, histogram, requestSizeRaw, postVsPatch] = settled.map(r => unwrap(r));

      setErrorRateData({
        labels: errorRate.map((d: any) => d.date),
        datasets: [
          {
            label: 'Error Rate (%)',
            data: errorRate.map((d: any) => d.errorRate),
            borderColor: 'rgb(220, 53, 69)',
            backgroundColor: 'rgba(220, 53, 69, 0.5)',
          },
        ],
      });

      // Load company data - sorted by request count
      const sortedByRequests = [...costByCompany].sort((a: any, b: any) => b.totalRequests - a.totalRequests);
      setCostData({
        labels: sortedByRequests.slice(0, 10).map((d: any) => d.companyName || d.companyId),
        datasets: [
          {
            label: 'Total Requests',
            data: sortedByRequests.slice(0, 10).map((d: any) => d.totalRequests),
            backgroundColor: [
              'rgba(255, 99, 132, 0.5)',
              'rgba(54, 162, 235, 0.5)',
              'rgba(255, 206, 86, 0.5)',
              'rgba(75, 192, 192, 0.5)',
              'rgba(153, 102, 255, 0.5)',
              'rgba(255, 159, 64, 0.5)',
              'rgba(199, 199, 199, 0.5)',
              'rgba(83, 102, 255, 0.5)',
              'rgba(255, 102, 178, 0.5)',
              'rgba(102, 255, 178, 0.5)',
            ],
          },
        ],
      });

      setPerformanceData({
        labels: performance.slice(0, 10).map((d: any) => d.companyName || d.companyId || 'Unknown'),
        datasets: [
          {
            label: 'Avg Response Time (ms)',
            data: performance.slice(0, 10).map((d: any) => d.avgTimeMs),
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
        ],
      });

      const processingTimes = aiProcessingTimes
        .map((d: any) => d.aiProcessingTime)
        .filter((time: number) => time > 0);
      setBoxPlotData({
        labels: [`AI Processing Time (${processingTimes.length} Requests)`],
        datasets: [
          {
            label: 'AI Processing Time (ms)',
            data: [processingTimes],
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 2,
            outlierColor: 'rgba(255, 99, 132, 1)',
            padding: 10,
            itemRadius: 0,
          },
        ],
      });

      setHistogramData({
        labels: histogram.map((d: any) => d.timeBucket),
        datasets: [
          {
            label: 'Number of Requests',
            data: histogram.map((d: any) => d.requestCount),
            backgroundColor: [
              'rgba(40, 167, 69, 0.7)',
              'rgba(75, 192, 192, 0.7)',
              'rgba(54, 162, 235, 0.7)',
              'rgba(255, 206, 86, 0.7)',
              'rgba(255, 159, 64, 0.7)',
              'rgba(255, 99, 132, 0.7)',
              'rgba(220, 53, 69, 0.7)',
            ],
            borderColor: [
              'rgba(40, 167, 69, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(255, 159, 64, 1)',
              'rgba(255, 99, 132, 1)',
              'rgba(220, 53, 69, 1)',
            ],
            borderWidth: 1,
          },
        ],
      });
      const sortedBySizeRaw = [...requestSizeRaw].sort((a: any, b: any) => Number(a.processedCount) - Number(b.processedCount));
      setRequestSizeCounts(sortedBySizeRaw.map((d: any) => Number(d.requestCount)));
      setRequestSizeData({
        labels: sortedBySizeRaw.map((d: any) =>
          Number(d.processedCount) === 0 ? '0 (cached)' : `${d.processedCount}`
        ),
        datasets: [
          {
            label: 'Avg Response Time (ms)',
            data: sortedBySizeRaw.map((d: any) => d.avgTimeMs),
            backgroundColor: 'rgba(153, 102, 255, 0.5)',
            borderColor: 'rgba(153, 102, 255, 1)',
            borderWidth: 1,
          },
        ],
      });

      // POST vs PATCH by company
      const sortedPostVsPatch = [...postVsPatch].sort((a: any, b: any) => b.totalCount - a.totalCount);
      setPostVsPatchData({
        labels: sortedPostVsPatch.map((d: any) => d.companyName || d.companyId || 'Unknown'),
        datasets: [
          {
            label: 'POST (Generate)',
            data: sortedPostVsPatch.map((d: any) => d.postCount),
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'PATCH (Update)',
            data: sortedPostVsPatch.map((d: any) => d.patchCount),
            backgroundColor: 'rgba(255, 159, 64, 0.7)',
            borderColor: 'rgba(255, 159, 64, 1)',
            borderWidth: 1,
          },
        ],
      });
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <p>Loading charts...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Analytics Dashboard</h2>

      <div style={styles.chartsGrid}>
        {/* AI Processing Time Box Plot */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>AI Processing Time Distribution (Excluding Cached Responses)</h3>
          {boxPlotData && (
            <div style={{ height: '360px', position: 'relative' }}>
            <Chart
              type={'boxplot' as any}
              data={boxPlotData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const data = context.parsed;
                        return [
                          `Min: ${data.min?.toFixed(2)} ms`,
                          `Q1: ${data.q1?.toFixed(2)} ms`,
                          `Median: ${data.median?.toFixed(2)} ms`,
                          `Q3: ${data.q3?.toFixed(2)} ms`,
                          `Max: ${data.max?.toFixed(2)} ms`,
                        ];
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Time (ms)',
                    },
                  },
                },
              }}
            />
            </div>
          )}
        </div>

        {/* Companies by Request Count Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Top 10 Companies by Request Count</h3>
          {costData && (
            <Pie
              data={costData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} requests (${percentage}%)`;
                      },
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Performance Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Average Response Time by Company (Top 10)</h3>
          {performanceData && (
            <Bar
              data={performanceData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Time (ms)',
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Response Time Histogram */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Response Time Distribution</h3>
          {histogramData && (
            <Bar
              data={histogramData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const value = context.parsed.y || 0;
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${value} requests (${percentage}%)`;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Number of Requests',
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Response Time',
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Avg Response Time by Processed Count Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Avg Response Time by Processed Count (POST only)</h3>
          {requestSizeData && (
            <Bar
              data={requestSizeData}
              plugins={[{
                id: 'requestCountLabels',
                afterDatasetsDraw(chart: any) {
                  const ctx = chart.ctx;
                  ctx.save();
                  ctx.font = 'bold 11px sans-serif';
                  ctx.fillStyle = '#555';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'bottom';
                  chart.getDatasetMeta(0).data.forEach((bar: any, i: number) => {
                    const count = requestSizeCounts[i];
                    if (count !== undefined) {
                      ctx.fillText(`n=${count}`, bar.x, bar.y - 4);
                    }
                  });
                  ctx.restore();
                },
              }]}
              options={{
                responsive: true,
                layout: { padding: { top: 20 } },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const i = context.dataIndex;
                        const count = requestSizeCounts[i];
                        return [
                          `Avg Response Time: ${context.parsed.y?.toFixed(0)} ms`,
                          `Requests: ${count}`,
                        ];
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: 'Avg Response Time (ms)' },
                  },
                  x: {
                    title: { display: true, text: 'Processed Count' },
                  },
                },
              }}
            />
          )}
        </div>

        {/* POST vs PATCH by Company */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>POST vs PATCH Requests by Company</h3>
          {postVsPatchData && (
            <Bar
              data={postVsPatchData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y || 0;
                        return `${label}: ${value}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    title: { display: true, text: 'Company' },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                    },
                  },
                  y: {
                    stacked: true,
                    beginAtZero: true,
                    title: { display: true, text: 'Request Count' },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Error Rate Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Error Rate Trend</h3>
          {errorRateData && (
            <Line
              data={errorRateData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Error Rate (%)',
                    },
                  },
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
  },
  title: {
    marginBottom: '20px',
    color: '#333',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
  },
  chartCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  chartTitle: {
    marginTop: 0,
    marginBottom: '20px',
    fontSize: '16px',
    color: '#555',
  },
};

export default ChartDashboard;

